import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useLazyFetchCurrentUserQuery } from '../../auth/authApiSlice';
import { selectToken, selectUser, setCredentials } from '../../auth/authSlice';
import { useSendEmailVerificationMutation } from '../certificationApiSlice';

interface EmailConfirmationGateProps {
  playerId: string;
  onConfirmed?: () => void;
  variant?: 'modal' | 'page';
}

type ResendState =
  | 'idle'
  | 'loading'
  | 'sent'
  | 'already_confirmed'
  | 'rate_limited'
  | 'send_failed';

type RecheckState = 'idle' | 'loading' | 'still_unconfirmed' | 'failed';

export function EmailConfirmationGate({
  playerId,
  onConfirmed,
  variant = 'page',
}: EmailConfirmationGateProps) {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const token = useSelector(selectToken);
  const [resendState, setResendState] = useState<ResendState>('idle');
  const [recheckState, setRecheckState] = useState<RecheckState>('idle');
  const [sendEmailVerification] = useSendEmailVerificationMutation();
  const [fetchCurrentUser] = useLazyFetchCurrentUserQuery();

  async function handleRecheck() {
    setRecheckState('loading');
    try {
      const freshUser = await fetchCurrentUser().unwrap();
      if (token) {
        dispatch(
          setCredentials({
            token,
            user: {
              _id: freshUser._id,
              email: freshUser.email,
              name: freshUser.name,
              image: freshUser.image,
              extra: freshUser.extra,
              apiKey: user?.apiKey,
            },
          }),
        );
      }

      if (freshUser.extra?.emailConfirmed === false) {
        setRecheckState('still_unconfirmed');
        return;
      }

      setRecheckState('idle');
      onConfirmed?.();
    } catch {
      setRecheckState('failed');
    }
  }

  async function handleResend() {
    setResendState('loading');
    try {
      const response = await sendEmailVerification({ playerId }).unwrap();
      switch (response.status) {
        case 'sent':
          setResendState('sent');
          break;
        case 'already_confirmed':
          setResendState('already_confirmed');
          onConfirmed?.();
          break;
        case 'rate_limited':
          setResendState('rate_limited');
          break;
        default:
          setResendState('send_failed');
      }
    } catch {
      setResendState('send_failed');
    }
  }

  const content = (
    <div className="flex flex-col items-center gap-6 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
        <svg
          className="h-8 w-8 text-amber-600"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
          />
        </svg>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-slate-900">
          Confirme seu e-mail
        </h2>
        <p className="mt-2 max-w-md text-sm text-slate-600">
          Você precisa confirmar seu e-mail antes de iniciar a prova. Verifique
          sua caixa de entrada ou solicite um novo link abaixo.
        </p>
      </div>

      {resendState === 'sent' && (
        <div className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
          E-mail enviado! Verifique sua caixa de entrada.
        </div>
      )}

      {resendState === 'rate_limited' && (
        <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Aguarde alguns minutos antes de solicitar outro link.
        </div>
      )}

      {resendState === 'send_failed' && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          Não foi possível enviar o e-mail. Tente novamente em breve.
        </div>
      )}

      {resendState === 'already_confirmed' && (
        <div className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
          Seu e-mail já está confirmado! Atualizando...
        </div>
      )}

      {recheckState === 'still_unconfirmed' && (
        <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Ainda não identificamos a confirmação. Confirme pelo link no e-mail e
          tente novamente.
        </div>
      )}

      {recheckState === 'failed' && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          Não foi possível verificar agora. Tente novamente em breve.
        </div>
      )}

      {resendState !== 'already_confirmed' && (
        <div className="flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={handleRecheck}
            disabled={recheckState === 'loading'}
            className="rounded-lg bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {recheckState === 'loading'
              ? 'VERIFICANDO...'
              : 'JÁ CONFIRMEI MEU E-MAIL'}
          </button>

          <button
            type="button"
            onClick={handleResend}
            disabled={resendState === 'loading'}
            className="text-sm font-semibold text-slate-600 underline hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {resendState === 'loading'
              ? 'Enviando...'
              : 'Reenviar link de confirmação'}
          </button>
        </div>
      )}
    </div>
  );

  if (variant === 'modal') {
    return content;
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto max-w-2xl rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
        {content}
      </div>
    </div>
  );
}
